type Subscriber = (history: number[], currentSpeed: number) => void;

interface ActiveDownload {
  startTime: number;
  bytesLoaded: number;
  lastBytesLoaded: number;
  lastTime: number;
  currentSpeed: number;
}

class NetworkService {
  private static instance: NetworkService | null = null;
  private subscribers: Set<Subscriber> = new Set();
  private history: number[] = [];
  private activeDownloads: Map<number, ActiveDownload> = new Map();
  private downloadIdCounter = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private peakSpeed = 100;

  private constructor() {}

  static getInstance(): NetworkService {
    if (!NetworkService.instance) {
      NetworkService.instance = new NetworkService();
    }
    return NetworkService.instance;
  }

  init(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    this.originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await this.originalFetch!(input, init);

      const clonedResponse = response.clone();

      this.trackDownload(clonedResponse);

      return response;
    };

    this.originalXHROpen = XMLHttpRequest.prototype.open;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null
    ) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const xhr = this;
      const downloadId = self.downloadIdCounter++;

      const download: ActiveDownload = {
        startTime: performance.now(),
        bytesLoaded: 0,
        lastBytesLoaded: 0,
        lastTime: performance.now(),
        currentSpeed: 0,
      };

      xhr.addEventListener('loadstart', () => {
        self.activeDownloads.set(downloadId, download);
      });

      xhr.addEventListener('progress', (event) => {
        if (event.lengthComputable || event.loaded > 0) {
          download.bytesLoaded = event.loaded;

          const now = performance.now();
          const elapsed = (now - download.lastTime) / 1000;

          if (elapsed >= 0.05) {
            const bytesPerSec =
              (download.bytesLoaded - download.lastBytesLoaded) / elapsed;
            download.currentSpeed = bytesPerSec / 1024; // KB/s
            download.lastBytesLoaded = download.bytesLoaded;
            download.lastTime = now;
          }
        }
      });

      xhr.addEventListener('loadend', () => {
        self.activeDownloads.delete(downloadId);
      });

      return self.originalXHROpen!.call(
        xhr,
        method,
        url,
        async ?? true,
        username,
        password
      );
    };

    this.intervalId = setInterval(() => {
      this.updateHistory();
    }, 100);
  }

  private async trackDownload(response: Response): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) return;

    const downloadId = this.downloadIdCounter++;
    const download: ActiveDownload = {
      startTime: performance.now(),
      bytesLoaded: 0,
      lastBytesLoaded: 0,
      lastTime: performance.now(),
      currentSpeed: 0,
    };

    this.activeDownloads.set(downloadId, download);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        download.bytesLoaded += value.length;

        const now = performance.now();
        const totalElapsed = (now - download.startTime) / 1000;

        if (totalElapsed > 0.01) {
          download.currentSpeed = download.bytesLoaded / totalElapsed / 1024; // KB/s
        }
      }
    } catch {
    } finally {
      setTimeout(() => {
        this.activeDownloads.delete(downloadId);
      }, 200);
    }
  }

  private updateHistory(): void {
    let totalSpeed = 0;
    for (const download of this.activeDownloads.values()) {
      totalSpeed += download.currentSpeed;
    }

    let normalizedValue: number;

    if (totalSpeed > 0) {
      if (totalSpeed > this.peakSpeed) {
        this.peakSpeed = totalSpeed;
      }

      const multiplier = 0.8 + Math.random() * 0.4;
      normalizedValue = (totalSpeed * multiplier) / this.peakSpeed;
      normalizedValue = Math.min(Math.max(normalizedValue, 0), 1);
    } else {
      const lastValue =
        this.history.length > 0 ? this.history[this.history.length - 1] : 0;
      normalizedValue = lastValue * 0.85;
      if (normalizedValue < 0.01) normalizedValue = 0;
    }

    this.history = [...this.history.slice(-500), normalizedValue];

    for (const subscriber of this.subscribers) {
      subscriber(this.history, totalSpeed);
    }
  }

  subscribe(callback: Subscriber): () => void {
    this.init();

    this.subscribers.add(callback);

    callback(this.history, this.history[this.history.length - 1] || 0);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  getHistory(): number[] {
    return this.history;
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.originalFetch && typeof window !== 'undefined') {
      window.fetch = this.originalFetch;
    }
    if (this.originalXHROpen && typeof window !== 'undefined') {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
    }
    this.subscribers.clear();
    this.activeDownloads.clear();
    this.history = [];
    this.initialized = false;
  }
}

export const networkService = NetworkService.getInstance();

if (typeof window !== 'undefined') {
  networkService.init();
}
